// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import AccountModal from "./AccountModal";
import { useNavigate } from "react-router-dom";
import { isReadOnlyTeacherDemo } from "../utils/demoAccess";

const menuStyle = {
  position: "relative",
  display: "inline-block",
};

const buttonStyle = {
  background: "#3256a2",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  width: 40,
  height: 40,
  fontWeight: 600,
  fontSize: 18,
  cursor: "pointer",
  marginLeft: 16,
};

const dropdownStyle = {
  position: "absolute",
  right: 0,
  top: 44,
  background: "#fff",
  color: "#23272f",
  borderRadius: 8,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  minWidth: 180,
  zIndex: 100,
  padding: "8px 0",
};

const itemStyle = {
  padding: "12px 24px",
  cursor: "pointer",
  fontSize: 16,
  borderBottom: "1px solid #f0f0f0",
};

const ProfileMenu = () => {
  const { user, logout, switchSchool, switchRole } = useAuth();
  const [open, setOpen] = useState<any>(false);
  const [modalOpen, setModalOpen] = useState<any>(false);
  const [switching, setSwitching] = useState<any>(false);
  const [switchError, setSwitchError] = useState<any>('');
  const closeTimerRef = useRef<any>(null);
  const navigate = useNavigate();

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const closeMenuWithDelay = useCallback((delay = 550) => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, delay);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  if (!user) return null;

  const isAdmin = user.roles && (user.roles.includes("admin") || user.roles.includes("school_admin") || user.roles.includes("district_admin"));
  const isSuperAdmin = user.roles && user.roles.includes("superadmin");
  const teacherRole = ["teacher", "special_services_teacher"].find(role => user.roles?.includes(role));
  const familyRole = ["parent", "family"].find(role => user.roles?.includes(role));
  const accountViews = teacherRole && familyRole ? [
    { role: teacherRole, label: "Teacher", home: "/teacher/home", active: ["teacher", "special_services_teacher"].includes(user.role) },
    { role: familyRole, label: "Family", home: "/parent/home", active: ["parent", "family"].includes(user.role) },
  ] : [];
  // Schools this user can switch to (exclude the currently active one)
  const otherSchools = (user.managed_schools || []).filter(
    (s) => s.school_id !== user.school_id
  );

  const handleSwitchSchool = async (schoolId) => {
    setSwitching(true);
    setSwitchError('');
    const result = await switchSchool(schoolId);
    setSwitching(false);
    if (result.ok) {
      setOpen(false);
      // Navigate to the role dashboard without a full page reload
      const role = user?.role || (user?.roles || [])[0];
      if (role === 'superadmin') navigate('/superadmin/home');
      else if (role === 'admin' || role === 'school_admin' || role === 'district_admin') navigate('/admin');
      else if (role === 'teacher' || role === 'special_services_teacher') navigate('/teacher/home');
      else if (role === 'student') navigate('/student/home');
      else if (role === 'parent' || role === 'family') navigate('/parent/home');
      else navigate('/dashboard');
    } else {
      setSwitchError(result.error);
    }
  };

  const handleSwitchRole = (view) => {
    setSwitchError('');
    const result = switchRole(view.role);
    if (result.ok) {
      clearCloseTimer();
      setOpen(false);
      navigate(view.home);
    } else {
      setSwitchError(result.error);
    }
  };

  return (
    <div 
      style={menuStyle}
      onMouseEnter={openMenu}
      onMouseLeave={() => closeMenuWithDelay()}
    >
      <button
        style={buttonStyle}
        aria-label="User menu"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            closeMenuWithDelay(0);
          } else {
            openMenu();
          }
        }}
      >
        {user.first_name ? user.first_name[0] : "U"}
      </button>
      {open && (
        <div
          style={{ ...dropdownStyle, minWidth: 220 }}
          onMouseEnter={openMenu}
          onMouseLeave={() => closeMenuWithDelay()}
        >
          <div style={itemStyle}>
            <strong>{user.first_name} {user.last_name}</strong><br />
            <span style={{ fontSize: 13, color: "#888" }}>{user.email}</span><br />
            {user.school_name && (
              <span style={{ fontSize: 12, color: "#3256a2", fontWeight: 600 }}>
                {user.school_name}
              </span>
            )}
          </div>

          {accountViews.length > 0 && (
            <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
                Switch account view
              </div>
              {accountViews.map(view => (
                <button
                  key={view.role}
                  type="button"
                  disabled={view.active || switching}
                  aria-pressed={view.active}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "8px", border: "none", borderRadius: 4,
                    background: view.active ? "#eff6ff" : "transparent",
                    color: "#3256a2", fontSize: 14,
                    fontWeight: view.active ? 700 : 400,
                    cursor: view.active || switching ? "default" : "pointer",
                  }}
                  onClick={() => handleSwitchRole(view)}
                >
                  {view.active ? `${view.label} (current)` : `Switch to ${view.label}`}
                </button>
              ))}
            </div>
          )}

          {/* School switcher — shown when admin has >1 school or is superadmin */}
          {(otherSchools.length > 0 || isSuperAdmin) && (
            <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>
                Switch School
              </div>
              {otherSchools.map((s) => (
                <div
                  key={s.school_id}
                  style={{
                    padding: "6px 8px",
                    cursor: switching ? "default" : "pointer",
                    borderRadius: 4,
                    fontSize: 14,
                    color: "#3256a2",
                    opacity: switching ? 0.5 : 1,
                  }}
                  onClick={() => !switching && handleSwitchSchool(s.school_id)}
                >
                  {s.school_name || `School #${s.school_id}`}
                </div>
              ))}
            </div>
          )}
          {switchError && (
            <div role="alert" style={{ padding: "8px 16px", fontSize: 12, color: "#d32f2f" }}>{switchError}</div>
          )}

          {isAdmin && (
            <div
              style={itemStyle}
              onClick={() => {
                navigate("/admin/requests");
                closeMenuWithDelay(0);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              Requests
            </div>
          )}
          {!isReadOnlyTeacherDemo(user) && <div
            style={itemStyle}
            onClick={() => {
              setModalOpen(true);
              closeMenuWithDelay(0);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            Manage Account
          </div>}
          <div 
            style={{ ...itemStyle, borderBottom: "none", color: "#d32f2f" }} 
            onClick={() => {
              closeMenuWithDelay(0);
              logout();
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            Log Out
          </div>
        </div>
      )}
      <AccountModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default ProfileMenu;
